<script lang="ts" setup>
import { onMounted, ref } from 'vue';

import { useLongScroll } from '@libs/utils';

interface AppSnapScrollProps {
  direction: 'X' | 'Y';
  scrollSpacer?: boolean;
}
const props = defineProps<AppSnapScrollProps>();

interface AppSnapScrollEmits {
  (eventName: 'long-scroll'): void;
  (eventName: 'init', element: HTMLElement): void;
}
const emit = defineEmits<AppSnapScrollEmits>();

const contentRef = ref<HTMLElement | undefined>();

const { longScrollEvent } = useLongScroll(contentRef);

longScrollEvent.subscribe(() => emit('long-scroll'));

onMounted(() => {
  if (contentRef.value) {
    emit('init', contentRef.value);
  }
});
</script>

<template>
  <div
    class="app-snap-scroll"
    :class="{
      'app-snap-scroll-x': props.direction === 'X',
      'app-snap-scroll-y': props.direction === 'Y',
    }"
  >
    <slot />
    <div ref="contentRef" class="app-snap-scroll-content">
      <slot name="scrollable" />
      <div v-if="props.scrollSpacer" class="scroll-spacer" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.app-snap-scroll {
  @import '../../styles/_media.scss';
  @import '../../styles/_mixins.scss';

  overflow: hidden;

  &.app-snap-scroll-y {
    > .app-snap-scroll-content {
      width: 100%;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      display: flex;
      flex-direction: column;

      -webkit-scroll-snap-type: y mandatory;
      -ms-scroll-snap-type: y mandatory;
      scroll-snap-type: y mandatory;

      @include for-less-or-equal-than-1200-px {
        @include no-scroll();
      }
      @include for-more-or-equal-than-1200-px {
        padding-right: var(--spacing-4);
      }
    }
    .scroll-spacer {
      height: 100vh;
      flex-shrink: 0;
    }
    :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
      flex-shrink: 0;
      -webkit-scroll-snap-align: start;
      scroll-snap-align: start;
    }
  }

  &.app-snap-scroll-x {
    > .app-snap-scroll-content {
      overflow-x: auto;
      overflow-y: hidden;
      display: flex;
      height: 100%;
      justify-content: flex-start;

      -webkit-scroll-snap-type: x mandatory;
      -ms-scroll-snap-type: x mandatory;
      scroll-snap-type: x mandatory;

      @include for-less-or-equal-than-1200-px {
        @include no-scroll();
      }
    }

    :deep(.app-snap-scroll-content) > *:not(.scroll-spacer) {
      flex-shrink: 0;
      -webkit-scroll-snap-align: start;
      scroll-snap-align: start;
    }
  }
}
</style>
